import * as THREE from "three";
import { SceneManager } from "../render/SceneManager";
import type { PlacedInstance } from "../render/InstancedRenderer";
import { BLOCK_SIZE, BASE_GROUND_OFFSET, SNOW_HEIGHT_FACTOR } from "../config";
import { BLOCK, type BiomeMap, type VoxelWorld } from "../types";
import { BIOME_RULES } from "./biomeRules";

let capGeom: THREE.BoxGeometry | null = null;

const dummy = new THREE.Object3D();
const white = new THREE.Color(1, 1, 1);

export function addSnowLayer(
    scene: SceneManager,
    world: VoxelWorld,
    heights: number[][],
    biomes: BiomeMap,
    options: { trackInstances?: boolean } = {},
): PlacedInstance[] {
    const trackInstances = options.trackInstances ?? true;
    const snowHeight = BLOCK_SIZE * SNOW_HEIGHT_FACTOR;
    const N = heights.length;
    const half = (N * BLOCK_SIZE) / 2;

    const spots: { x: number; yBlock: number; z: number }[] = [];

    // Pass 1: snow caps on the ground top of every column (snowy biomes).
    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            const biome = biomes.biome[x][z];
            const rules = BIOME_RULES[biome];
            if (!rules?.snowLayerAlways) continue;

            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            const topPlusOne = world.get(x, topAbs + 1, z);
            const isBlocked =
                topPlusOne === BLOCK.Water ||
                topPlusOne === BLOCK.Ice ||
                topPlusOne === BLOCK.Log ||
                topPlusOne === BLOCK.Leaves ||
                topPlusOne === BLOCK.PineLeaves;
            if (isBlocked) continue;

            const hasSnowBlock = world.get(x, topAbs + 1, z) === BLOCK.SnowBlock;
            spots.push({ x, yBlock: topAbs + (hasSnowBlock ? 1 : 0), z });
        }
    }

    // Pass 2: snow caps on the topmost leaf of each (x,z) column for trees in
    // snowy biomes. Gives canopies a frosted look without burying the leaves.
    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            const biome = biomes.biome[x][z];
            const rules = BIOME_RULES[biome];
            if (!rules?.snowLayerAlways) continue;

            let topLeafY = -1;
            for (let y = world.height - 1; y >= 0; y--) {
                const block = world.get(x, y, z);
                if (block === BLOCK.Leaves || block === BLOCK.PineLeaves) {
                    topLeafY = y;
                    break;
                }
            }
            if (topLeafY < 0) continue;
            if (world.get(x, topLeafY + 1, z)) continue; // something already on top
            spots.push({ x, yBlock: topLeafY, z });
        }
    }

    if (spots.length === 0) return [];

    capGeom ??= new THREE.BoxGeometry(BLOCK_SIZE, snowHeight, BLOCK_SIZE);
    scene.sharedGeometries.add(capGeom);

    const mesh = new THREE.InstancedMesh(
        capGeom,
        scene.materials[BLOCK.SnowBlock].material,
        spots.length,
    );
    mesh.instanceColor = new THREE.InstancedBufferAttribute(
        new Float32Array(spots.length * 3),
        3,
    );

    const placed: PlacedInstance[] = [];
    for (let i = 0; i < spots.length; i++) {
        const { x, yBlock, z } = spots[i];
        const px = x * BLOCK_SIZE - half + BLOCK_SIZE / 2;
        const py = yBlock * BLOCK_SIZE + snowHeight + BLOCK_SIZE / 2;
        const pz = z * BLOCK_SIZE - half + BLOCK_SIZE / 2;
        dummy.position.set(px, py, pz);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        mesh.setColorAt(i, white);
        if (trackInstances) placed.push({ mesh, index: i, px, py, pz });
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor.needsUpdate = true;
    scene.group.add(mesh);

    return placed;
}
